import React, {
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  analyzeFrame,
  deriveGuidance,
  validateFrameQuality,
  GuidanceMessage,
} from "./quality/analyzeFrame";
import { loadOpenCV } from "./quality/opencv";
import { detectDocumentContour } from "./quality/contour";
import {
  validateGeometry,
  geometryGuidance,
  GeometryResult,
} from "./quality/geometry";
import { applyPerspectiveCorrection } from "./quality/perspective";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";

export interface MRZCameraModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  documentType?: "TD1" | "TD3";
}

interface MRZGuideRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type CameraState =
  | { status: "STREAMING" }
  | { status: "CAPTURED"; blob: Blob; previewUrl: string }
  | { status: "ERROR"; message: string };

interface Geometry {
  /** Guide rect in DISPLAY pixels (canvas coordinate space). */
  guide: MRZGuideRect;
  /** MRZ sub-zone in DISPLAY pixels. */
  mrz: MRZGuideRect;
  /**
   * Guide rect mapped to the <video> element's intrinsic resolution.
   * Used by triggerCapture so the crop covers the correct passport region.
   * DO NOT use guide (display px) here — drawImage() uses video coordinates.
   */
  videoGuide: MRZGuideRect;
  isLandscape: boolean;
  isSmall: boolean;
}

const NO_CAMERA_MESSAGE =
  "Camera not available. Please upload a photo instead.";

const ANALYSIS_INTERVAL_MS = 250; // 4 FPS analysis; overlay still draws at 60 FPS

interface FinalValidationResult {
  passed: boolean;
  reason?: string;
}

async function validateCapturedImage(
  blob: Blob,
): Promise<FinalValidationResult> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return { passed: false, reason: "Could not decode image" };
  }

  if (bitmap.width < 400 || bitmap.height < 300) {
    bitmap.close();
    return { passed: false, reason: "Image resolution too low" };
  }

  const VW = 128,
    VH = 96;
  const canvas = document.createElement("canvas");
  canvas.width = VW;
  canvas.height = VH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    bitmap.close();
    return { passed: true }; // cannot validate without a canvas context; let it through
  }
  ctx.drawImage(bitmap, 0, 0, VW, VH);
  bitmap.close();

  const { data } = ctx.getImageData(0, 0, VW, VH);
  const lums = new Float32Array(VW * VH);
  for (let i = 0; i < data.length; i += 4) {
    lums[i >> 2] = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
  }

  // Use the TD3 MRZ band position (~bottom 33% of frame).
  const mrzStartRow = Math.floor(VH * 0.67);

  const quality = validateFrameQuality(lums, VW, VH, mrzStartRow);

  if (quality.blur.isTooBlurred) {
    return {
      passed: false,
      reason: "Image is too blurry — hold the camera still while scanning",
    };
  }
  if (quality.glare.isSevere) {
    return {
      passed: false,
      reason:
        "Glare detected over passport text — avoid direct light and retake",
    };
  }
  if (quality.exposure.score < 0.1) {
    return {
      passed: false,
      reason: "Poor exposure — adjust lighting and retake",
    };
  }
  if (quality.localContrast.mrzLocalContrast < 5) {
    return {
      passed: false,
      reason: "MRZ text area has insufficient contrast",
    };
  }

  return { passed: true };
}

const ALIGN_ACCENT = "#FFFFFF";
const STABLE_ACCENT = "#4CAF50";
const CAPTURED_ACCENT = "#2196F3";

const GUIDE_FRAME_ASPECT = 1.32;
const MIN_GW = 240;
const MIN_GH = 169;
const JPEG_QUALITY = 0.92;
const STABLE_MS = 3_000;
const MIN_CAPTURE_WIDTH_DESKTOP = 1000;
const MIN_CAPTURE_WIDTH_MOBILE = 560;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const mapDisplayRectToCoveredVideo = (
  rect: MRZGuideRect,
  displayW: number,
  displayH: number,
  videoW: number,
  videoH: number,
): MRZGuideRect => {
  // object-fit: cover — a plain x/y scale maps to wrong source pixels on iPhone
  const scale = Math.max(displayW / videoW, displayH / videoH);
  const renderedW = videoW * scale;
  const renderedH = videoH * scale;
  const cropX = Math.max(0, (renderedW - displayW) / 2);
  const cropY = Math.max(0, (renderedH - displayH) / 2);

  const x = clamp((rect.x + cropX) / scale, 0, videoW - 1);
  const y = clamp((rect.y + cropY) / scale, 0, videoH - 1);

  return {
    x,
    y,
    w: Math.max(1, Math.min(rect.w / scale, videoW - x)),
    h: Math.max(1, Math.min(rect.h / scale, videoH - y)),
  };
};

function computeMrzPresenceScore(
  lums: Float32Array,
  w: number,
  h: number,
  mrzStartRow: number,
): number {
  const bandH = h - mrzStartRow;
  if (bandH < 12) return 0;

  let darkPixels = 0;
  let edgePixels = 0;
  let total = 0;

  for (let y = mrzStartRow; y < h; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lum = lums[i];

      if (lum < 120) darkPixels++;

      const gx = Math.abs(lums[i + 1] - lums[i - 1]);
      if (gx > 28) edgePixels++;

      total++;
    }
  }

  const darkRatio = darkPixels / total;
  const edgeRatio = edgePixels / total;

  // MRZ should have many dark strokes, but not be a fully black area.
  const darkScore =
    darkRatio >= 0.04 && darkRatio <= 0.35
      ? 1
      : darkRatio < 0.04
        ? darkRatio / 0.04
        : Math.max(0, 1 - (darkRatio - 0.35) / 0.25);

  // MRZ text creates lots of vertical edges.
  const edgeScore = Math.min(1, edgeRatio / 0.12);

  return darkScore * 0.45 + edgeScore * 0.55;
}

const canvasToJpegBlob = (
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject): void => {
    canvas.toBlob(
      (blob): void => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Unable to encode camera frame"));
      },
      "image/jpeg",
      quality,
    );
  });

/**
 * Re-encode `blob` rotated clockwise by `degrees` (0/90/180/270). Always derived
 * from the ORIGINAL capture so repeated rotations don't compound JPEG loss. The
 * canvas dimensions swap for 90°/270° so nothing is clipped. Returns the original
 * blob for 0° and `null` if decoding/encoding fails.
 */
async function rotateJpegBlob(
  blob: Blob,
  degrees: number,
): Promise<Blob | null> {
  const normalized = ((Math.round(degrees / 90) * 90) % 360 + 360) % 360;
  if (normalized === 0) return blob;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null;
  }

  const w = bitmap.width;
  const h = bitmap.height;
  const swap = normalized === 90 || normalized === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? h : w;
  canvas.height = swap ? w : h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return null;
  }
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((normalized * Math.PI) / 180);
  ctx.drawImage(bitmap, -w / 2, -h / 2);
  bitmap.close();

  try {
    return await canvasToJpegBlob(canvas, JPEG_QUALITY);
  } catch {
    return null;
  }
}

const accentForStatus = (status: CameraState["status"]): string =>
  status === "CAPTURED" ? CAPTURED_ACCENT : ALIGN_ACCENT;

const initialOrientation = (): { isLandscape: boolean; isSmall: boolean } => {
  if (typeof window === "undefined") {
    return { isLandscape: false, isSmall: false };
  }
  return {
    isLandscape: window.innerWidth > window.innerHeight,
    isSmall: window.innerHeight < 600,
  };
};

const MRZCameraModal: React.FC<MRZCameraModalProps> = ({
  open,
  onClose,
  onCapture,
  documentType = "TD3",
}) => {
  const [state, setState] = useState<CameraState>({ status: "STREAMING" });
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const [capturing, setCapturing] = useState<boolean>(false);
  // Clockwise rotation (0/90/180/270) the user applied to the captured preview.
  const [rotation, setRotation] = useState<number>(0);
  const [rotatedPreview, setRotatedPreview] = useState<{
    url: string;
    blob: Blob;
  } | null>(null);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [orientation, setOrientation] = useState<{
    isLandscape: boolean;
    isSmall: boolean;
  }>(initialOrientation);
  const [setupAttempt, setSetupAttempt] = useState<number>(0);

  const restartingRef = useRef(false);

  const mountedRef = useRef(false);
  const openRef = useRef(open);
  const rotatedPreviewRef = useRef<{ url: string; blob: Blob } | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capturingRef = useRef<boolean>(false);
  const stateRef = useRef<CameraState>(state);
  const geometryRef = useRef<Geometry | null>(geometry);
  const stableFromRef = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const guidanceRef = useRef<GuidanceMessage>(
    "Place your passport inside the frame" as GuidanceMessage,
  );
  const cvRef = useRef<unknown>(null);
  const detectedQuadRef = useRef<{
    corners: readonly [number, number][];
    isValid: boolean;
  } | null>(null);
  const geomQuadRef = useRef<{
    corners: readonly [number, number][];
    geomW: number;
    geomH: number;
  } | null>(null);
  const documentTypeRef = useRef(documentType);
  useEffect((): void => {
    documentTypeRef.current = documentType;
  }, [documentType]);

  useEffect((): void => {
    stateRef.current = state;
  }, [state]);

  useEffect((): void => {
    geometryRef.current = geometry;
  }, [geometry]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // SECTION: ResizeObserver + guideRect computation ------------------------
  const computeGeometry = useCallback((): void => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!canvas) return;

    const vw = canvas.offsetWidth;
    const vh = canvas.offsetHeight;
    if (vw <= 0 || vh <= 0) return;
    // fix[2] / fix[7]: do not commit geometry until the intrinsic video size is known.
    if (video && video.videoWidth <= 0) return;

    // Keep canvas intrinsic size in lockstep with its CSS box so 1 unit = 1 px.
    if (canvas.width !== vw) canvas.width = vw;
    if (canvas.height !== vh) canvas.height = vh;

    // Use the modal canvas size, not window.innerHeight — iPhone Safari's layout
    // viewport includes browser chrome which puts controls outside visible area.
    const isLandscape = vw > vh;
    const isSmall = vh < 600;
    const topReserve = isLandscape
      ? Math.max(56, vh * 0.1)
      : Math.max(112, vh * 0.18);
    const bottomReserve = isLandscape
      ? Math.max(56, vh * 0.1)
      : Math.max(132, vh * 0.22);
    const availableHeight = Math.max(90, vh - topReserve - bottomReserve);

    let gw: number;
    if (isLandscape) {
      gw = Math.min(vw * 0.66, availableHeight * 0.82 * GUIDE_FRAME_ASPECT);
    } else {
      gw = Math.min(vw * 0.88, availableHeight * 0.56 * GUIDE_FRAME_ASPECT);
    }
    let gh = gw / GUIDE_FRAME_ASPECT;

    if (gw < MIN_GW) {
      gw = MIN_GW;
      gh = gw / GUIDE_FRAME_ASPECT;
    }
    if (gh < MIN_GH) {
      gh = MIN_GH;
      gw = gh * GUIDE_FRAME_ASPECT;
    }

    // Prevent the guide from overflowing tiny viewports/webviews.
    const maxGw = Math.max(120, vw * 0.94);
    const maxGh = Math.max(90, availableHeight * 0.86);

    if (gw > maxGw) {
      gw = maxGw;
      gh = gw / GUIDE_FRAME_ASPECT;
    }
    if (gh > maxGh) {
      gh = maxGh;
      gw = gh * GUIDE_FRAME_ASPECT;
    }

    const gx = (vw - gw) / 2;
    const gy = topReserve + (availableHeight - gh) / 2;

    // fix[7]: TD1 cards need a taller MRZ band than TD3 booklets.
    const mrzH = Math.round(gh * (documentType === "TD1" ? 0.35 : 0.22));
    const mrzW = gw;
    const mrzX = gx;
    const mrzY = gy + gh - mrzH;

    const guide: MRZGuideRect = { x: gx, y: gy, w: gw, h: gh };
    const mrz: MRZGuideRect = { x: mrzX, y: mrzY, w: mrzW, h: mrzH };

    let videoGuide: MRZGuideRect = guide;
    if (video && video.videoWidth > 0 && video.videoHeight > 0) {
      videoGuide = mapDisplayRectToCoveredVideo(
        guide,
        vw,
        vh,
        video.videoWidth,
        video.videoHeight,
      );
    }

    // fix[2]: only commit corrected video-space geometry once it is valid.
    setGeometry({ guide, mrz, videoGuide, isLandscape, isSmall });
    setOrientation({ isLandscape, isSmall });
  }, [documentType]);

  useEffect((): (() => void) | void => {
    if (!open) return;

    computeGeometry();

    let raf: number | null = null;
    const queueComputeGeometry = (): void => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame((): void => {
        raf = null;
        computeGeometry();
      });
    };

    const root = rootRef.current;
    const observer =
      root && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(queueComputeGeometry)
        : null;
    if (root && observer) observer.observe(root);

    // Safari's visible viewport changes as browser chrome expands/collapses;
    // ResizeObserver alone does not always remeasure it.
    window.addEventListener("resize", queueComputeGeometry);
    window.addEventListener("orientationchange", queueComputeGeometry);
    window.visualViewport?.addEventListener("resize", queueComputeGeometry);
    window.visualViewport?.addEventListener("scroll", queueComputeGeometry);

    return (): void => {
      if (raf !== null) cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", queueComputeGeometry);
      window.removeEventListener("orientationchange", queueComputeGeometry);
      window.visualViewport?.removeEventListener(
        "resize",
        queueComputeGeometry,
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        queueComputeGeometry,
      );
    };
  }, [open, computeGeometry]);

  // SECTION: triggerCapture -------------------------------------------------
  const triggerCapture = useCallback(async (): Promise<void> => {
    if (capturingRef.current) return;
    if (stateRef.current.status === "CAPTURED") return;
    const video = videoRef.current;
    const currentGeometry = geometryRef.current;
    if (!video || !currentGeometry) return;
    // fix[2]: reject stale display-space geometry before cropping video pixels.
    if (
      video.videoWidth <= 0 ||
      video.videoHeight <= 0 ||
      currentGeometry.videoGuide.w <= 0 ||
      currentGeometry.videoGuide.h <= 0
    ) {
      return;
    }
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw <= 0 || vh <= 0) return;

    capturingRef.current = true;
    setCapturing(true);
    try {
      const vg = currentGeometry.videoGuide;
      const sx = clamp(Math.floor(vg.x), 0, vw - 1);
      const sy = clamp(Math.floor(vg.y), 0, vh - 1);
      const sw = Math.max(1, Math.min(Math.floor(vg.w), vw - sx));
      const sh = Math.max(1, Math.min(Math.floor(vg.h), vh - sy));

      // fix[1] / fix[8]: deliver the crop, and keep the preview on the same JPEG path.
      const crop = document.createElement("canvas");
      crop.width = sw;
      crop.height = sh;
      const cropCtx = crop.getContext("2d");
      if (!cropCtx) return;
      cropCtx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

      // ── Optional perspective correction (requires OpenCV + valid geometry) ─
      let finalCanvas: HTMLCanvasElement = crop;
      const savedGeomQuad = geomQuadRef.current;
      if (cvRef.current && savedGeomQuad) {
        try {
          const corrected = applyPerspectiveCorrection(
            cvRef.current,
            crop,
            savedGeomQuad.corners,
            savedGeomQuad.geomW,
            savedGeomQuad.geomH,
            documentTypeRef.current,
          );
          if (corrected) finalCanvas = corrected;
        } catch {
          // Non-fatal — use the original crop.
        }
      }

      const cropBlob = await canvasToJpegBlob(finalCanvas, JPEG_QUALITY);

      const previewUrl = URL.createObjectURL(cropBlob);

      if (!mountedRef.current || !openRef.current) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      // fix[1]: send the crop itself to the parent so preview and delivery match.
      setState({ status: "CAPTURED", blob: cropBlob, previewUrl });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("MRZ capture failed", err);
      }
    } finally {
      capturingRef.current = false;
      setCapturing(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      const cur = stateRef.current;
      if (cur.status === "CAPTURED") {
        URL.revokeObjectURL(cur.previewUrl);
      }
    };
  }, []);

  // --- Camera setup / teardown --------------------------------------------
  useEffect((): (() => void) | void => {
    if (!open) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      setState({ status: "ERROR", message: NO_CAMERA_MESSAGE });
      return;
    }

    let cancelled = false;
    let activeStream: MediaStream | null = null;
    const trackEndedHandlers: Array<{
      track: MediaStreamTrack;
      handler: () => void;
    }> = [];
    const initialVideo = videoRef.current;
    let attachedVideo: HTMLVideoElement | null = null;
    let onLoadedMeta: (() => void) | null = null;

    const onTrackEnded = (): void => {
      if (cancelled || restartingRef.current) return;
      setState({ status: "ERROR", message: NO_CAMERA_MESSAGE });
    };

    const tryGetStream = async (): Promise<MediaStream> => {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1440 },
            aspectRatio: { ideal: 4 / 3 },
          },
        });
      } catch {
        return navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
          },
        });
      }
    };

    const setup = async (): Promise<void> => {
      try {
        const stream = await tryGetStream();
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        activeStream = stream;
        streamRef.current = stream;

        const v = videoRef.current;
        if (!v) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        v.srcObject = stream;
        attachedVideo = v;

        // fix[2]: commit geometry from loadedmetadata only after intrinsic size is known.
        onLoadedMeta = (): void => {
          if (cancelled) return;
          computeGeometry();
        };
        v.addEventListener("loadedmetadata", onLoadedMeta);

        stream.getTracks().forEach((track): void => {
          track.addEventListener("ended", onTrackEnded);
          trackEndedHandlers.push({ track, handler: onTrackEnded });
        });

        try {
          await v.play();
        } catch {
          /* autoplay policies / interruptions are non-fatal here */
        }

        if (!cancelled && rootRef.current) {
          rootRef.current.focus();
        }
      } catch {
        if (cancelled) return;
        setState({ status: "ERROR", message: NO_CAMERA_MESSAGE });
      }
    };

    void setup();

    return (): void => {
      cancelled = true;
      trackEndedHandlers.forEach(({ track, handler }) => {
        track.removeEventListener("ended", handler);
      });
      if (activeStream) {
        const [track] = activeStream.getVideoTracks();
        if (track) {
          // fix[5]: switch torch off before stopping the track.
          track
            .applyConstraints({
              advanced: [{ torch: false } as MediaTrackConstraintSet],
            })
            .catch(() => {
              /* non-fatal */
            });
        }
        activeStream.getTracks().forEach((t) => t.stop());
      }
      if (streamRef.current === activeStream) {
        streamRef.current = null;
      }
      const v = attachedVideo ?? initialVideo;
      if (v && onLoadedMeta) {
        v.removeEventListener("loadedmetadata", onLoadedMeta);
      }
      if (v) {
        v.srcObject = null;
      }
    };
  }, [open, setupAttempt, computeGeometry]);

  // --- OpenCV.js — load once on first open, reuse on subsequent opens ------
  useEffect((): void => {
    if (!open) return;
    loadOpenCV()
      .then((cv): void => {
        cvRef.current = cv;
      })
      .catch((err: unknown): void => {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            "OpenCV.js failed to load — geometry detection disabled:",
            err,
          );
        }
      });
  }, [open]);

  // --- Reset state on close (and revoke any captured preview) -------------
  useEffect((): void => {
    if (open) return;
    const cur = stateRef.current;
    if (cur.status === "CAPTURED") {
      URL.revokeObjectURL(cur.previewUrl);
    }
    capturingRef.current = false;
    setCapturing(false);
    setGeometry(null);
    setState({ status: "STREAMING" });
    setRotation(0);
    setRotatedPreview(null);
    setIsRotating(false);
  }, [open]);

  // Keep a ref in sync so handleUseThis reads the latest rotated blob.
  useEffect((): void => {
    rotatedPreviewRef.current = rotatedPreview;
  }, [rotatedPreview]);

  // --- Derive the rotated preview from the ORIGINAL captured blob ----------
  useEffect((): (() => void) | void => {
    if (state.status !== "CAPTURED" || rotation === 0) {
      setRotatedPreview(null);
      setIsRotating(false);
      return;
    }

    let cancelled = false;
    let url: string | null = null;
    setIsRotating(true);

    void rotateJpegBlob(state.blob, rotation).then((blob): void => {
      if (cancelled) return;
      if (!blob) {
        setIsRotating(false);
        return;
      }
      url = URL.createObjectURL(blob);
      setRotatedPreview({ url, blob });
      setIsRotating(false);
    });

    return (): void => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [state, rotation]);

  const rotateLeft = useCallback((): void => {
    setRotation((r) => (r + 270) % 360);
  }, []);

  const rotateRight = useCallback((): void => {
    setRotation((r) => (r + 90) % 360);
  }, []);

  // SECTION: drawOverlay() — overlay canvas rAF loop -----------------------
  useEffect((): (() => void) | void => {
    if (!open) return;
    if (state.status === "ERROR" || state.status === "CAPTURED") return;

    let raf: number | null = null;

    const drawOverlay = (): void => {
      const canvas = overlayCanvasRef.current;
      if (!canvas || !geometry) {
        raf = requestAnimationFrame(drawOverlay);
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        raf = requestAnimationFrame(drawOverlay);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cur = stateRef.current;
      const accent = accentForStatus(cur.status);
      const { x: gx, y: gy, w: gw, h: gh } = geometry.guide;
      const { x: mrzX, y: mrzY, w: mrzW, h: mrzH } = geometry.mrz;

      // Step 1 — Dark vignette outside guide
      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Step 2 — Punch transparent window for the passport guide rect
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0, 0, 0, 1)";
      ctx.beginPath();
      const roundRect = ctx.roundRect;

      if (typeof roundRect === "function") {
        roundRect.call(ctx, gx, gy, gw, gh, 6);
      } else {
        ctx.rect(gx, gy, gw, gh);
      }
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();

      // Step 3 — MRZ sub-zone tint (re-darken the strip inside the window)
      ctx.fillStyle = "rgba(0, 0, 0, 0.28)";
      ctx.fillRect(mrzX, mrzY, mrzW, mrzH);

      // Step 4 — MRZ sub-zone top dashed separator
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(mrzX, mrzY);
      ctx.lineTo(mrzX + mrzW, mrzY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Step 5 — MRZ label centred inside the sub-zone
      ctx.font = "500 11px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = accent;
      ctx.globalAlpha = 0.9;
      ctx.fillText("MRZ zone", mrzX + mrzW / 2, mrzY + mrzH / 2);
      ctx.globalAlpha = 1;

      // Step 6 — Corner brackets on the PASSPORT guide rect
      let bracketLen = Math.round(Math.min(gw, gh) * 0.1);
      bracketLen = Math.max(20, Math.min(bracketLen, 36));
      ctx.lineWidth = 3;
      ctx.strokeStyle = accent;
      ctx.lineCap = "round";
      ctx.setLineDash([]);

      ctx.beginPath();
      ctx.moveTo(gx, gy + bracketLen);
      ctx.lineTo(gx, gy);
      ctx.lineTo(gx + bracketLen, gy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(gx + gw - bracketLen, gy);
      ctx.lineTo(gx + gw, gy);
      ctx.lineTo(gx + gw, gy + bracketLen);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(gx, gy + gh - bracketLen);
      ctx.lineTo(gx, gy + gh);
      ctx.lineTo(gx + bracketLen, gy + gh);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(gx + gw, gy + gh - bracketLen);
      ctx.lineTo(gx + gw, gy + gh);
      ctx.lineTo(gx + gw - bracketLen, gy + gh);
      ctx.stroke();

      ctx.lineCap = "butt";

      // Step 7 — Inward chevrons at midpoint of each side (alignment guides)
      ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";

      // Top — pointing DOWN (∨)
      {
        const cx = gx + gw / 2;
        const cy = gy + 14;
        ctx.beginPath();
        ctx.moveTo(cx - 8, cy - 5);
        ctx.lineTo(cx, cy + 5);
        ctx.lineTo(cx + 8, cy - 5);
        ctx.stroke();
      }
      // Bottom — pointing UP (∧)
      {
        const cx = gx + gw / 2;
        const cy = gy + gh - 14;
        ctx.beginPath();
        ctx.moveTo(cx - 8, cy + 5);
        ctx.lineTo(cx, cy - 5);
        ctx.lineTo(cx + 8, cy + 5);
        ctx.stroke();
      }
      // Left — pointing RIGHT (>)
      {
        const cx = gx + 14;
        const cy = gy + gh / 2;
        ctx.beginPath();
        ctx.moveTo(cx - 5, cy - 8);
        ctx.lineTo(cx + 5, cy);
        ctx.lineTo(cx - 5, cy + 8);
        ctx.stroke();
      }
      // Right — pointing LEFT (<)
      {
        const cx = gx + gw - 14;
        const cy = gy + gh / 2;
        ctx.beginPath();
        ctx.moveTo(cx + 5, cy - 8);
        ctx.lineTo(cx - 5, cy);
        ctx.lineTo(cx + 5, cy + 8);
        ctx.stroke();
      }
      ctx.lineCap = "butt";

      // Step 8 — Detected document quad outline (updated by the analysis loop)
      const detectedQuad = detectedQuadRef.current;
      if (detectedQuad && detectedQuad.corners.length === 4) {
        ctx.save();
        ctx.strokeStyle = detectedQuad.isValid
          ? "rgba(76, 175, 80, 0.85)" // green — geometry valid
          : "rgba(255, 193, 7, 0.80)"; // amber — geometry issue
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 3]);
        ctx.lineJoin = "round";
        ctx.beginPath();
        const [qc0, qc1, qc2, qc3] = detectedQuad.corners;
        ctx.moveTo(qc0[0], qc0[1]);
        ctx.lineTo(qc1[0], qc1[1]);
        ctx.lineTo(qc2[0], qc2[1]);
        ctx.lineTo(qc3[0], qc3[1]);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }

      // fix[15]: expose the auto-capture countdown as a thin arc around the shutter.
      if (stableFromRef.current !== null) {
        const now = performance.now(); // must match the rAF clock used by the analysis loop
        const stableProgressMs = Math.min(
          STABLE_MS,
          Math.max(0, now - stableFromRef.current),
        );
        const progress = stableProgressMs / STABLE_MS;
        const shutterSize = orientation.isLandscape ? 64 : 72;
        const shutterCenterX = orientation.isLandscape
          ? canvas.width - 28 - shutterSize / 2
          : canvas.width / 2;
        const shutterCenterY = orientation.isLandscape
          ? canvas.height / 2
          : canvas.height - 24 - shutterSize / 2;

        ctx.save();
        ctx.strokeStyle = STABLE_ACCENT;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.arc(
          shutterCenterX,
          shutterCenterY,
          shutterSize / 2 + 10,
          -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * progress,
        );
        ctx.stroke();
        ctx.restore();

        // Draw countdown number
        const remainingSeconds = Math.ceil(
          (STABLE_MS - stableProgressMs) / 1000,
        );
        if (remainingSeconds > 0) {
          ctx.save();
          ctx.font = "500 20px system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = STABLE_ACCENT;
          ctx.fillText(
            remainingSeconds.toString(),
            shutterCenterX,
            shutterCenterY,
          );
          ctx.restore();
        }
      }

      raf = requestAnimationFrame(drawOverlay);
    };

    raf = requestAnimationFrame(drawOverlay);

    return (): void => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [open, state.status, geometry, orientation.isLandscape]);

  // SECTION: Auto-capture — heuristic frame analysis loop ---------------------
  useEffect((): (() => void) | void => {
    if (!open || state.status !== "STREAMING") return;

    // ── Tuning constants ──────────────────────────────────────────────────
    const GUIDE_SW = 128; // analysis canvas width
    const GUIDE_SH = 96; // analysis canvas height (4:3 ≈ guide aspect)

    const isMobileLike =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(pointer: coarse)").matches ||
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

    const DIFF_THRESHOLD = isMobileLike ? 20 : 8;
    const ROLLING_DIFF_THRESHOLD = isMobileLike ? 17 : DIFF_THRESHOLD * 0.75;

    const CONF_START = isMobileLike ? 0.52 : 0.62;
    const CONF_RESET = isMobileLike ? 0.28 : 0.42;

    const UNSTABLE_GRACE_MS = isMobileLike ? 1100 : 180;
    const PROGRESS_DECAY_MS = isMobileLike ? 0.25 : 0.9;

    const MRZ_FRACS =
      documentType === "TD1" ? [0.35, 0.4, 0.45] : [0.24, 0.28, 0.33];

    const MID_START = Math.floor(GUIDE_SH * 0.15);
    const MID_END = Math.floor(GUIDE_SH * 0.72);

    // ── Offscreen analysis canvas ─────────────────────────────────────────
    const guideCanvas = document.createElement("canvas");
    guideCanvas.width = GUIDE_SW;
    guideCanvas.height = GUIDE_SH;
    const guideCtx = guideCanvas.getContext("2d", { willReadFrequently: true });

    // ── Geometry detection canvas (larger than analysis for better contours)
    const GEOM_SW = 320;
    const GEOM_SH = 240;
    const geomCanvas = document.createElement("canvas");
    geomCanvas.width = GEOM_SW;
    geomCanvas.height = GEOM_SH;
    const geomCtx = geomCanvas.getContext("2d", { willReadFrequently: true });
    // MRZ band start row in geometry canvas coordinates.
    const geomMrzStartRow = Math.floor(GEOM_SH * (1 - Math.max(...MRZ_FRACS)));

    // ── Reusable pixel buffers (double-buffered for frame diff) ───────────
    const prevBufA = new Uint8ClampedArray(GUIDE_SW * GUIDE_SH * 4);
    const prevBufB = new Uint8ClampedArray(GUIDE_SW * GUIDE_SH * 4);
    const recentDiffs = new Float32Array(8);

    // ── Closure-local mutable state (no useState → no re-renders) ─────────
    let rafId: number | null = null;

    let stableFrom: number | null = null;
    let stableProgressMs = 0;
    let lastAnalysisAt = 0;
    let unstableSince: number | null = null;

    let useCurrentBuf = 0;
    let hasPrevSample = false;
    let recentDiffIndex = 0;
    let recentDiffCount = 0;
    let recentDiffSum = 0;

    const tick = (now: number): void => {
      if (capturingRef.current) {
        stableFrom = null;
        stableFromRef.current = null;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const video = videoRef.current;
      const geo = geometryRef.current;

      if (
        !video ||
        !geo ||
        !guideCtx ||
        video.videoWidth <= 0 ||
        video.videoHeight <= 0 ||
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
      ) {
        stableFrom = null;
        stableFromRef.current = null;
        hasPrevSample = false;
        rafId = requestAnimationFrame(tick);
        return;
      }

      // ── Throttle: run analysis at ~4 FPS; overlay still draws at 60 FPS ──
      if (now - lastAnalysisAt < ANALYSIS_INTERVAL_MS) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const dt =
        lastAnalysisAt > 0
          ? Math.min(300, now - lastAnalysisAt)
          : ANALYSIS_INTERVAL_MS;
      lastAnalysisAt = now;

      // Sample videoGuide region into the small analysis canvas.
      const { x, y, w, h } = geo.videoGuide;
      try {
        guideCtx.drawImage(video, x, y, w, h, 0, 0, GUIDE_SW, GUIDE_SH);
      } catch {
        stableFrom = null;
        stableFromRef.current = null;
        hasPrevSample = false;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const curr = guideCtx.getImageData(0, 0, GUIDE_SW, GUIDE_SH).data;
      const lums = new Float32Array(GUIDE_SW * GUIDE_SH);

      for (let i = 0; i < curr.length; i += 4) {
        lums[i >> 2] =
          (curr[i] * 77 + curr[i + 1] * 150 + curr[i + 2] * 29) >> 8;
      }
      const currentBuf = useCurrentBuf === 0 ? prevBufA : prevBufB;
      const previousBuf = useCurrentBuf === 0 ? prevBufB : prevBufA;
      currentBuf.set(curr);

      // Compute rolling mean diff from previous frames before calling analyzeFrame.
      const rollingMeanDiff =
        recentDiffCount > 0 ? recentDiffSum / recentDiffCount : 99;

      // ── Modular analysis ──────────────────────────────────────────────────
      const result = analyzeFrame({
        pixels: currentBuf,
        prevPixels: hasPrevSample ? previousBuf : null,
        guideW: GUIDE_SW,
        guideH: GUIDE_SH,
        midStart: MID_START,
        midEnd: MID_END,
        mrzFracs: MRZ_FRACS,
        rollingMeanDiff,
        diffThreshold: DIFF_THRESHOLD,
        rollingDiffThreshold: ROLLING_DIFF_THRESHOLD,
      });
      const mrzStartRow = Math.floor(GUIDE_SH * (1 - Math.max(...MRZ_FRACS)));

      const mrzPresenceScore = computeMrzPresenceScore(
        lums,
        GUIDE_SW,
        GUIDE_SH,
        mrzStartRow,
      );

      const mrzPresenceOk = mrzPresenceScore >= (isMobileLike ? 0.52 : 0.62);

      // Advance rolling diff ring buffer with this frame's diff.
      if (hasPrevSample) {
        const outgoing = recentDiffs[recentDiffIndex];
        recentDiffSum -= outgoing;
        recentDiffs[recentDiffIndex] = result.frameDiff;
        recentDiffSum += result.frameDiff;
        recentDiffIndex = (recentDiffIndex + 1) % recentDiffs.length;
        if (recentDiffCount < recentDiffs.length) recentDiffCount += 1;
      }

      useCurrentBuf ^= 1;
      hasPrevSample = true;

      // ── Geometry detection (best-effort; falls back if OpenCV not ready) ──
      let geometryResult: GeometryResult | null = null;
      if (cvRef.current && geomCtx) {
        try {
          geomCtx.drawImage(video, x, y, w, h, 0, 0, GEOM_SW, GEOM_SH);
          const geomImageData = geomCtx.getImageData(0, 0, GEOM_SW, GEOM_SH);
          const { quad } = detectDocumentContour(cvRef.current, geomImageData);
          if (quad) {
            geometryResult = validateGeometry(
              quad,
              GEOM_SW,
              GEOM_SH,
              documentType,
              geomMrzStartRow,
            );
            // Map corners to display coordinates for the overlay RAF.
            detectedQuadRef.current = {
              corners: quad.corners.map(
                ([px, py]) =>
                  [
                    geo.guide.x + (px / GEOM_SW) * geo.guide.w,
                    geo.guide.y + (py / GEOM_SH) * geo.guide.h,
                  ] as [number, number],
              ),
              isValid: geometryResult.isValid,
            };
            // Store in geom-canvas coords for perspective correction in triggerCapture.
            geomQuadRef.current = {
              corners: quad.corners,
              geomW: GEOM_SW,
              geomH: GEOM_SH,
            };
          } else {
            detectedQuadRef.current = null;
            geomQuadRef.current = null;
          }
        } catch {
          detectedQuadRef.current = null;
          geomQuadRef.current = null;
        }
      }

      // ── Update guidance: geometry takes priority over quality signals ─────
      const qualityGuidance = deriveGuidance(result, DIFF_THRESHOLD);
      guidanceRef.current =
        geometryResult && !geometryResult.isValid
          ? geometryGuidance(geometryResult)
          : qualityGuidance;

      // ── Confidence: substitute real geometry score when OpenCV is ready ───
      const { mrzScore: mrzBandScore, stabilityScore } = result;
      const geometryScore = geometryResult?.geometryScore ?? mrzBandScore;
      const confidence = cvRef.current
        ? result.scoreBreakdown.blur * 0.25 +
          result.scoreBreakdown.glare * 0.2 +
          result.scoreBreakdown.exposure * 0.15 +
          result.scoreBreakdown.contrast * 0.15 +
          geometryScore * 0.25
        : result.confidence;
      const stableFrame = stabilityScore === 1;

      // ── Min crop resolution gate for auto-capture ─────────────────────────
      const minCaptureWidth = isMobileLike
        ? MIN_CAPTURE_WIDTH_MOBILE
        : MIN_CAPTURE_WIDTH_DESKTOP;
      const resolutionReady = geo.videoGuide.w >= minCaptureWidth;

      // ── Timer with hysteresis ─────────────────────────────────────────────
      const mrzReady = mrzBandScore >= 0.45 && mrzPresenceOk;
      const geometryOk = !geometryResult || geometryResult.isValid;

      const goodFrame =
        resolutionReady &&
        mrzReady &&
        stableFrame &&
        confidence >= CONF_START &&
        !result.hardReject &&
        geometryOk;
      const badFrame =
        result.hardReject ||
        !mrzReady ||
        confidence < CONF_RESET ||
        (geometryResult !== null &&
          !geometryResult.isValid &&
          geometryResult.geometryScore < 0.3);

      if (badFrame) {
        stableProgressMs = 0;
        stableFrom = null;
        unstableSince = null;
        stableFromRef.current = null;
      } else if (goodFrame) {
        unstableSince = null;

        if (stableFrom === null) stableFrom = now - stableProgressMs;

        stableProgressMs = Math.min(STABLE_MS, stableProgressMs + dt);

        if (stableProgressMs >= STABLE_MS && !capturingRef.current) {
          stableProgressMs = 0;
          stableFrom = null;
          stableFromRef.current = null;
          void triggerCapture();
          return;
        }

        stableFromRef.current = now - stableProgressMs;
      } else {
        // Hysteresis zone: MRZ/confidence are okay-ish, but phone moved a bit.
        if (unstableSince === null) unstableSince = now;

        const unstableFor = now - unstableSince;

        if (unstableFor > UNSTABLE_GRACE_MS) {
          stableProgressMs = Math.max(
            0,
            stableProgressMs - dt * PROGRESS_DECAY_MS,
          );
        }

        if (stableProgressMs <= 0) {
          stableFrom = null;
          stableFromRef.current = null;
        } else {
          stableFrom = now - stableProgressMs;
          stableFromRef.current = stableFrom;
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return (): void => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      stableFromRef.current = null;
    };
  }, [open, state.status, triggerCapture, documentType]);

  const handleRetake = useCallback((): void => {
    if (stateRef.current.status === "CAPTURED") {
      URL.revokeObjectURL(stateRef.current.previewUrl);
    }
    setRotation(0);
    setRotatedPreview(null);
    setIsRotating(false);
    setState({ status: "STREAMING" });
  }, []);

  const handleUseThis = useCallback((): void => {
    const cur = stateRef.current;
    if (cur.status !== "CAPTURED") return;
    // Deliver the rotated blob when the user adjusted orientation, else the original.
    const blob = rotatedPreviewRef.current?.blob ?? cur.blob;
    const file = new File([blob], "passport-scan.jpg", {
      type: "image/jpeg",
    });
    // Final validation before delivering the capture to the caller.
    void validateCapturedImage(blob).then((validation) => {
      if (!mountedRef.current) return;
      if (!validation.passed) {
        // Stub: log only — extend here to show an error and force retake.
        if (process.env.NODE_ENV !== "production") {
          console.warn("MRZ capture validation failed:", validation.reason);
        }
      }
      onCapture(file);
      onClose();
    });
    // fix[9]: let the existing close cleanup revoke the preview URL.
  }, [onCapture, onClose]);

  const handleTryAgain = useCallback((): void => {
    restartingRef.current = true;

    // fix[10]: stop the active stream synchronously before starting over.
    const activeStream = streamRef.current;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    const video = videoRef.current;
    if (video) {
      video.srcObject = null;
    }

    setState({ status: "STREAMING" });
    setGeometry(null);
    setSetupAttempt((n) => n + 1);
    setCapturing(false);
    capturingRef.current = false;

    queueMicrotask(() => {
      restartingRef.current = false;
    });
  }, []);

  const handleRootKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (
        (e.code === "Space" || e.code === "Enter") &&
        stateRef.current.status === "STREAMING"
      ) {
        e.preventDefault();
        void triggerCapture();
        return;
      }
      if (e.code === "Escape") {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose, triggerCapture],
  );

  // Show the rotated result once ready; fall back to the original while rotating
  // (the previous rotated object URL is revoked on each rotation change).
  const capturedPreviewUrl =
    state.status === "CAPTURED"
      ? !isRotating && rotatedPreview
        ? rotatedPreview.url
        : state.previewUrl
      : "";

  const showLiveSurface = state.status === "STREAMING";
  const showInstructionStrip =
    showLiveSurface && orientation.isSmall && !orientation.isLandscape;
  const liveGuidance = guidanceRef.current;
  const guidanceTone =
    liveGuidance === "Ready"
      ? "success"
      : liveGuidance === "Hold steady" || liveGuidance === "Move closer"
        ? "warning"
        : "default";
  const cameraStatusLabel =
    state.status === "STREAMING"
      ? "Live"
      : state.status === "CAPTURED"
        ? "Captured"
        : "Issue";
  const cameraStatusBg =
    state.status === "STREAMING"
      ? "rgba(59,130,246,0.18)"
      : state.status === "CAPTURED"
        ? "rgba(16,185,129,0.18)"
        : "rgba(239,68,68,0.18)";

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="mrz-modal-title">
      <Box
        ref={rootRef}
        role="dialog"
        aria-labelledby="mrz-modal-title"
        tabIndex={0}
        onKeyDown={handleRootKeyDown}
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          left: 0,
          bottom: "auto",
          height: "100vh",
          "@supports (height: 100dvh)": {
            height: "100dvh",
          },
          bgcolor: "#050A14",
          background:
            "radial-gradient(1200px 800px at 50% 0%, rgba(59,130,246,0.24), transparent 55%), linear-gradient(180deg, #0A1120 0%, #04070D 100%)",
          outline: "none",
          overflow: "hidden",
        }}
      >
        {/* Always-mounted live surface (hidden in CAPTURED/ERROR states) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: showLiveSurface ? "block" : "none",
            background:
              "linear-gradient(180deg, rgba(4,9,18,0.12) 0%, rgba(4,9,18,0.56) 100%)",
          }}
        >
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <Box
            component="canvas"
            ref={overlayCanvasRef}
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* fix[14]: keep a single accessible modal title instead of duplicate labels. */}
        <Box
          component="h2"
          id="mrz-modal-title"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
          }}
        >
          Passport scanner
        </Box>

        {showLiveSurface && (
          <Stack
            sx={{
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 12px)",
              left: orientation.isLandscape ? 12 : { xs: 12, md: 24 },
              right: orientation.isLandscape ? "auto" : { xs: 12, md: 24 },
              width: orientation.isLandscape ? "min(46vw, 340px)" : "auto",
              zIndex: 5,
              gap: 0.75,
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: "rgba(10, 16, 28, 0.5)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(12px)",
                display: "inline-flex",
                alignSelf: "flex-start",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DocumentScannerOutlinedIcon
                sx={{ color: "rgba(255,255,255,0.8)", fontSize: 16 }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                Scan passport
              </Typography>
              <Box
                sx={{
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#fff",
                  bgcolor: cameraStatusBg,
                  lineHeight: 1.2,
                }}
              >
                {cameraStatusLabel}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: "rgba(10, 16, 28, 0.5)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(12px)",
                color: "#fff",
                display: "inline-flex",
                alignSelf: "flex-start",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor:
                    guidanceTone === "success"
                      ? "rgba(16,185,129,0.9)"
                      : guidanceTone === "warning"
                        ? "rgba(245,158,11,0.9)"
                        : "rgba(255,255,255,0.6)",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {liveGuidance}
              </Typography>
            </Paper>
          </Stack>
        )}

        {/* Top-right close (live state only) */}
        {showLiveSurface && (
          <IconButton
            aria-label="Close scanner"
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 12px)",
              right: { xs: 12, md: 24 },
              color: "#fff",
              bgcolor: "rgba(10,16,28,0.7)",
              border: "1px solid rgba(255,255,255,0.12)",
              zIndex: 6,
              backdropFilter: "blur(12px)",
              "&:hover": { bgcolor: "rgba(10,16,28,0.85)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {/* Shutter Fab — orientation-aware position */}
        {showLiveSurface && (
          <Fab
            size="large"
            aria-label="Capture passport photo"
            // fix[12]: disable the shutter while capture is in flight.
            aria-busy={capturing}
            disabled={capturing}
            color="default"
            onClick={(): void => {
              void triggerCapture();
            }}
            sx={{
              position: "absolute",
              transition: "all 200ms ease",
              zIndex: 6,
              ...(orientation.isLandscape
                ? {
                    right: 28,
                    top: "50%",
                    left: "auto",
                    bottom: "auto",
                    transform: "translateY(-50%)",
                    width: 64,
                    height: 64,
                  }
                : {
                    bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                    left: "50%",
                    right: "auto",
                    top: "auto",
                    transform: "translateX(-50%)",
                    width: 72,
                    height: 72,
                  }),
              bgcolor: "rgba(255,255,255,0.96)",
              color: "#111",
              boxShadow:
                "0 16px 40px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.6)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,1)",
                transform: orientation.isLandscape
                  ? "translateY(-50%) scale(1.03)"
                  : "translateX(-50%) scale(1.03)",
              },
            }}
          >
            <CameraAltIcon sx={{ opacity: capturing ? 0.2 : 1 }} />
            {capturing && (
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  position: "absolute",
                  color: "#111",
                }}
              />
            )}
          </Fab>
        )}

        {/* Instruction card — small viewports */}
        {showInstructionStrip && (
          <Paper
            elevation={0}
            sx={{
              position: "absolute",
              bottom: orientation.isLandscape
                ? "calc(env(safe-area-inset-bottom, 0px) + 12px)"
                : "calc(env(safe-area-inset-bottom, 0px) + 116px)",
              left: "50%",
              transform: "translateX(-50%)",
              px: 2,
              py: 1,
              borderRadius: 99,
              bgcolor: "rgba(10,16,28,0.7)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              zIndex: 5,
              pointerEvents: "none",
            }}
          >
            <DocumentScannerOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
            <Typography
              variant="caption"
              sx={{ color: "#fff", whiteSpace: "nowrap", fontWeight: 600 }}
            >
              Fit the full passport in the frame
            </Typography>
          </Paper>
        )}

        {/* CAPTURED — preview + Retake / Use this */}
        {state.status === "CAPTURED" && (
          <Stack
            sx={{
              position: "absolute",
              inset: 0,
              p: { xs: 2, md: 3 },
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 4,
              bgcolor: "rgba(4, 7, 13, 0.78)",
            }}
          >
            <Paper
              sx={{
                width: "100%",
                maxWidth: 760,
                p: 2,
                borderRadius: 4,
                bgcolor: "rgba(10,16,28,0.78)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(16px)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  src={capturedPreviewUrl}
                  alt="Captured passport"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    borderRadius: 16,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
                    opacity: isRotating ? 0.45 : 1,
                    transition: "opacity 120ms ease",
                  }}
                />
                {isRotating && (
                  <CircularProgress
                    size={32}
                    sx={{ position: "absolute", top: "50%", left: "50%", mt: "-16px", ml: "-16px", color: "#fff" }}
                    aria-label="Rotating image"
                  />
                )}
              </Box>
              <Stack
                direction="row"
                justifyContent="center"
                spacing={1.5}
                sx={{ mt: 1.5 }}
              >
                <IconButton
                  aria-label="Rotate left"
                  onClick={rotateLeft}
                  disabled={isRotating}
                  sx={{
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <RotateLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="Rotate right"
                  onClick={rotateRight}
                  disabled={isRotating}
                  sx={{
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <RotateRightIcon />
                </IconButton>
              </Stack>
            </Paper>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ width: "100%", maxWidth: 420 }}
            >
              <Button
                variant="outlined"
                onClick={handleRetake}
                disabled={isRotating}
                sx={{ borderRadius: 999 }}
              >
                Retake
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleUseThis}
                disabled={isRotating}
                sx={{ borderRadius: 999 }}
              >
                Use this
              </Button>
            </Stack>
          </Stack>
        )}

        {/* ERROR */}
        {state.status === "ERROR" && (
          <Stack
            sx={{
              position: "absolute",
              inset: 0,
              p: { xs: 2, md: 3 },
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 4,
              bgcolor: "rgba(4, 7, 13, 0.82)",
            }}
          >
            <Paper
              sx={{
                width: "100%",
                maxWidth: 520,
                p: 2,
                borderRadius: 4,
                bgcolor: "rgba(10,16,28,0.78)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(16px)",
              }}
            >
              <Alert severity="error" sx={{ bgcolor: "transparent", p: 0 }}>
                {state.message}
              </Alert>
            </Paper>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ width: "100%", maxWidth: 420 }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
                sx={{ borderRadius: 999 }}
              >
                Upload instead
              </Button>
              <Button
                variant="contained"
                onClick={handleTryAgain}
                sx={{ borderRadius: 999 }}
              >
                Try again
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default MRZCameraModal;
